import Button from "../core/Button";
import Strings from "../core/data/Strings";
import { GameObject } from "../core/GameObject";
import ResizableScene from "../core/ResizableScene";
import ScaleManager from "../core/ScaleManager";
import GameColors from "../GameColors";
import ActionQueue from "../utils/ActionQueue";
import Fonts from "../utils/Fonts";
import FilmReelManager from "./FilmReelManager";

export class StoryScreen extends GameObject {
    index: number;
    textContainer: Phaser.GameObjects.Container;
    textBG: Phaser.GameObjects.Image;
    storyText: Phaser.GameObjects.Text;
    buttonContainer: Phaser.GameObjects.Container;
    playBtn: Button;
    buttonText: Phaser.GameObjects.Text;
    resize: () => void;
    filmReelManager: FilmReelManager;

    constructor(scene: ResizableScene) {
        super(scene);
        this.index = 0;
        const bg = this.addImage("shopBaseBG");

        const glow1 = this.addImage("shopBGGlow");
        const glow2 = this.addImage("shopBGGlow");

        const textContainer = this.textContainer = scene.add.container();
        this.add(textContainer);

        const filmReelManager = this.filmReelManager = new FilmReelManager(scene);
        this.add(filmReelManager);
        //filmReelManager.setScale(0.1);

        const popBG = scene.add.image(0, 0, "pauseBacker");
        textContainer.add(popBG);
        textContainer.setSize(popBG.width, popBG.height);

        const textBG = this.textBG = scene.add.image(0, 0, "storyTextBG");
        textContainer.add(textBG);

        const storyText = this.storyText = scene.add.text(0, 0, Strings.get("PlaceholderStoryText1"), {
            fontFamily: Fonts.MaliBoldItalic,
            fontSize: "36px",
            color: GameColors.BLACK,
            align: "center",
            wordWrap: { width: 620, useAdvancedWrap: true }
        });
        textContainer.add(storyText);

        const buttonContainer = this.buttonContainer = scene.add.container();
        textContainer.add(buttonContainer);
        const playBtn = this.playBtn = scene.add.button(0, 0, "shopBuyBtnLocked", undefined, this.nextPage, this, false);
        playBtn.scale = 2;
        buttonContainer.add(playBtn);
        buttonContainer.setSize(playBtn.displayWidth, playBtn.displayHeight);
        const buttonText = this.buttonText = scene.add.text(0, 0, Strings.get("Next"), {
            fontFamily: Fonts.LuckiestGuy,
            fontSize: "56px",
            color: GameColors.WHITE
        });
        buttonContainer.add(buttonText);

        playBtn.addPointerDownAnim(() => {
            scene.tweens.add({
                targets: buttonContainer,
                scale: { from: 1, to: 1.1 },
                yoyo: true,
                duration: 100,
            });
        });

        const logo = scene.add.image(0, 0, "splashLogo");
        textContainer.add(logo);
        logo.scale = 0.5;

        const skipBtn = scene.add.button(0, 0, "skipBtn", undefined, this.skipPressed, this, true);
        this.add(skipBtn);

        const skipText = scene.add.text(0, 0, Strings.get("Skip"), {
            fontFamily: Fonts.LuckiestGuy,
            fontSize: "24px",
            color: GameColors.WHITE,
        });
        this.add(skipText);

        const soundOn = !this.scene.soundManager.muted;
        const soundButtonKey = soundOn ? "soundButtonOn" : "soundButtonOff";
        const soundButton = this.scene.add.button(0, 0, soundButtonKey, undefined, () => this.toggleSound(soundButton), undefined, true);
        this.add(soundButton);

        this.resize = () => {
            ScaleManager.ScaleBackgroundFromZone(bg, scene.viewport);
            scene.alignIn.Center(bg, scene.viewport);
            const popupScale = Math.min(scene.viewport.width / 1920, 1);
            textContainer.scale = popupScale;
            // const alignScale = 1 - ((1 - popupScale) * 1);
            filmReelManager.scale = popupScale;
            scene.alignIn.Center(filmReelManager, scene.viewport, -400 * popupScale, 0);
            
            scene.alignIn.Center(textContainer, scene.viewport, 360 * popupScale, 0);
            scene.alignIn.Center(glow1, scene.viewport, (-400 * popupScale) - 250, 0);
            scene.alignIn.Center(textBG, popBG, 150, 0);
            scene.alignIn.Center(glow2, textContainer, textBG.x, -250);
            scene.alignIn.BottomCenter(buttonContainer, textBG, 0, 160);
            scene.alignIn.Center(buttonText, playBtn, 0, -10);
            scene.alignIn.Center(storyText, textBG, 0, 0);
            scene.alignIn.TopCenter(logo, textBG, 0, 415);
            scene.alignIn.TopRight(soundButton, scene.viewport, -34, -34);
            scene.alignIn.TopRight(skipBtn, scene.viewport, -150, -34);
            scene.alignTo.BottomCenter(skipText, skipBtn, 0, 0);
        };

        scene.addResize(this.resize);
        scene.resize();

        const queue = new ActionQueue();
        queue.add(complete => scene.time.delayedCall(500, complete));

        filmReelManager.goToFrame(12);
        filmReelManager.x -= 900;
        filmReelManager.y -= 6000;
        queue.add(complete => {
            this.scene.soundManager.play("filmReelSFX");
            this.scene.tweens.add({
                targets: filmReelManager,
                x: filmReelManager.x + 900,
                y: filmReelManager.y + 6000,
                duration: 200,
                ease: Phaser.Math.Easing.Quadratic.Out,
                onComplete: complete,
            });
        });

        textContainer.visible = false;
        textContainer.x -= 500;
        queue.add(complete => {
            textContainer.visible = true;
            this.scene.tweens.add({
                targets: textContainer,
                x: textContainer.x + 500,
                duration: 200,
                ease: Phaser.Math.Easing.Quadratic.Out,
                onComplete: complete,
            });
        });

        const logoScale = logo.scale;
        logo.scale = 0;
        buttonContainer.scale = 0;
        skipBtn.scale = 0;
        skipText.scale = 0;
        soundButton.scale = 0;
        queue.add(complete => {
            this.scene.tweens.add({
                targets: logo,
                scale: logoScale,
                duration: 300,
                ease: Phaser.Math.Easing.Back.Out,
                onComplete: complete,
            });

            this.scene.tweens.add({
                targets: buttonContainer,
                scale: 1,
                duration: 300,
                delay: 150,
                ease: Phaser.Math.Easing.Back.Out,
            });

            this.scene.tweens.add({
                targets: [skipBtn, skipText],
                scale: 1,
                duration: 100,
                ease: Phaser.Math.Easing.Quadratic.Out,
                onComplete: complete,
            });

            this.scene.tweens.add({
                targets: soundButton,
                scale: 1,
                duration: 100,
                delay: 50,
                ease: Phaser.Math.Easing.Quadratic.Out,
            });
        });

        queue.start();
    }

    destroy(): void {
        this.scene.removeResize(this.resize);
        super.destroy();
    }

    nextPage(): void {
        this.index++;

        // button swap on final page
        if (this.index == 2) {
            this.scene.tweens.killTweensOf(this.buttonContainer);
            
            const queue = new ActionQueue();
            queue.add(complete => {
                this.scene.tweens.add({
                    targets: this.buttonContainer,
                    scale: 0,
                    duration: 100,
                    ease: Phaser.Math.Easing.Quadratic.In,
                    onComplete: complete,
                });
            });
            queue.add(complete => {
                this.playBtn.setTexture("shopBuyBtn");
                this.buttonText.setText(Strings.get("Play"));
                this.resize();

                this.scene.tweens.add({
                    targets: this.buttonContainer,
                    scale: 1,
                    duration: 100,
                    ease: Phaser.Math.Easing.Quadratic.Out,
                    onComplete: complete,
                });
            })
            queue.start();
        }

        if(this.index <= 2) this.scene.soundManager.play("filmReelSFX");

        if (this.index > 2) {
            const spineContainer = this.scene.add.spineContainer(this.buttonContainer.x, this.buttonContainer.y);
            this.textContainer.addAt(spineContainer, this.textContainer.getIndex(this.buttonContainer)); // insert behind button
            const spine = this.scene.add.spine(0, 0, "explosion-generic");
            spine.scale = 0.5;
            spineContainer.add(spine);
            this.playBtn.disableInteractive();
            spine.play("hit", false);
            spine.on("complete", () => {
                this.skipPressed();
            });

            // this.skipPressed();
            return;
        } else {
            this.playBtn.disableInteractive();
            this.scene.time.addEvent({
                delay: 300,
                callback: () => this.playBtn.setInteractive()
            });
        }

        this.storyText.text = Strings.get(`PlaceholderStoryText${this.index + 1}`);
        this.scene.alignIn.Center(this.storyText, this.textBG, 0, 0);
        
        this.filmReelManager.switchTo(this.index);
        this.resize();

        const queue = new ActionQueue();
        this.scene.tweens.killTweensOf(this.textBG);
        this.textBG.angle = 0;
        queue.add(complete => {
            this.scene.tweens.add({
                targets: this.textBG,
                y: this.textBG.y - 10,
                duration: 100,
                yoyo: true,
            });

            this.scene.tweens.add({
                targets: this.textBG,
                angle: -3,
                duration: 100,
                onComplete: complete,
            });
        });

        queue.add(complete => {
            this.scene.tweens.add({
                targets: this.textBG,
                angle: 2,
                duration: 100,
                onComplete: complete,
            });
        });

        queue.add(complete => {
            this.scene.tweens.add({
                targets: this.textBG,
                angle: 0,
                duration: 100,
                onComplete: complete,
            });
        });
        queue.start();
    }


    toggleSound(btn: Button): void {
        this.scene.soundManager.muted = !this.scene.soundManager.muted;
        this.scene.sound.mute = this.scene.soundManager.muted;
        this.scene.music.muted = !this.scene.music.muted;
        if (!this.scene.soundManager.muted) btn.setTexture("soundButtonOn");
        else btn.setTexture("soundButtonOff");
    }


    skipPressed(): void {
        this.scene.scene.start("PreloaderScene", { destinationScene: "GameScene" });
    }

    exit(): void {
        this.destroy();
    }

}